import Framework from '@greenville/framework';
import { applySnapshot, types } from 'mobx-state-tree';
import * as constants from '../../../common/constants';

const evidenceObj = types.model('EvidenceObject', {
  quote: types.maybeNull(types.string),
  block_id: types.maybeNull(types.string)
});

const evidenceUnion = types.union(types.string, evidenceObj);

const topicObj = types.model('TopicObject', {
  topic_id: types.maybeNull(types.string),
  topic: types.maybeNull(types.string),
  short_topic: types.maybeNull(types.string),
  coverage_range: types.maybeNull(types.optional(types.array(types.string), [])),
  evidence_quotes: types.maybeNull(types.optional(types.array(evidenceUnion), [])),
  evidence_text: types.maybeNull(types.optional(types.array(types.string), [])),
  evidence_text_block_ids: types.maybeNull(types.optional(types.array(types.string), [])),
  injection_after_block_id: types.maybeNull(types.string),
  recommended_ingestion: types.maybeNull(types.string)
});

const feedbackObj = types.model('FeedbackObject', {
  comment: types.maybeNull(types.string),
  reason: types.maybeNull(types.optional(types.array(types.string), [])),
  addedBy: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string)
});

const aetTopicData = types.model('aetTopicData', {
  pageId: types.maybeNull(types.string),
  bookId: types.maybeNull(types.string),
  lifeCycleStatus: types.maybeNull(types.string),
  productModel: types.maybeNull(types.string),
  serverDateTime: types.maybeNull(types.string),
  updatedDate: types.maybeNull(types.union(types.string, types.number)),
  sourceURI: types.maybeNull(types.string),
  tenantId: types.maybeNull(types.string),
  version: types.maybeNull(types.string),
  topics: types.maybeNull(types.optional(types.array(topicObj), [])),
  feedbacks: types.maybeNull(types.optional(types.array(feedbackObj), [])),
  // Keep for backward compatibility
  modifiedBy: types.maybeNull(types.string)
});

const AetTopic = types
  .model('AetTopic', {
    data: types.optional(types.array(aetTopicData), []),
    rawResponse: types.maybeNull(types.frozen()),
    commentPlaceholder: types.optional(types.string, ''),
    commentPlaceholderFetched: types.optional(types.boolean, false)
  })
  .actions((self) => ({
    fetchAetTopic(data) {
      const payload = {
        ...data,
        aettopic: true
      };
      Framework.getEventManager().publish(constants.GET_CHATGPT_TOC_DATA, {
        payload
      });
    },
    setResponse(response) {
      // Preserve commentPlaceholder and fetch flag before applying snapshot
      const currentPlaceholder = self.commentPlaceholder;
      const wasFetched = self.commentPlaceholderFetched;
      applySnapshot(self, response);
      // Restore commentPlaceholder and fetch flag after applying snapshot
      if (currentPlaceholder) {
        // eslint-disable-next-line no-param-reassign
        self.commentPlaceholder = currentPlaceholder;
        // eslint-disable-next-line no-param-reassign
        self.commentPlaceholderFetched = wasFetched;
      }
      Framework.getEventManager().publish(constants.SET_AET_TOPIC_DATA);
    },
    resetStoreValues() {
      const initialState = [];
      applySnapshot(self.data, initialState);
      Framework.getEventManager().publish(constants.SET_AET_TOPIC_DATA);
    },
    addAetTopic(payload) {
      Framework.getEventManager().publish(constants.ADD_AET_TOPIC_DATA, {
        payload
      });
    },
    setAddAetTopicResponse(response) {
      Framework.getEventManager().publish(constants.SET_ADD_AET_TOPIC_DATA, {
        response
      });
    },
    updateAetTopic(payload) {
      Framework.getEventManager().publish(constants.UPDATE_AET_TOPIC_DATA, {
        payload
      });
    },
    setUpdateAetTopicResponse(response) {
      Framework.getEventManager().publish(constants.SET_UPDATE_AET_TOPIC_DATA, {
        response
      });
    },
    addAETComment(data) {
      const payload = {
        ...data,
        aettopic: true
      };
      Framework.getEventManager().publish(constants.ADD_MCQ_COMMENT, {
        payload
      });
    },
    deleteAetTopic(payload) {
      Framework.getEventManager().publish(constants.DELETE_AET_TOPIC_DATA, {
        payload
      });
    },
    setDeleteAetTopicResponse(response) {
      Framework.getEventManager().publish(constants.SET_DELETE_AET_TOPIC_DATA, {
        response
      });
    },
    fetchCommentPlaceholder(tenantId) {
      // Only fetch if not already fetched
      if (!self.commentPlaceholderFetched) {
        Framework.getEventManager().publish(constants.FETCH_AET_TOPIC_PLACEHOLDER, {
          payload: { tenantId }
        });
      }
    },
    setCommentPlaceholder(placeholderText) {
      // eslint-disable-next-line no-param-reassign
      self.commentPlaceholder = placeholderText;
      // eslint-disable-next-line no-param-reassign
      self.commentPlaceholderFetched = true;
      Framework.getEventManager().publish(constants.SET_AET_TOPIC_PLACEHOLDER);
    }
  }));

export default AetTopic;
